/*
  FLOMEC 3D Viewer — assets/flomec-3d-viewer.js
  Behavior for snippets/flomec__3d-viewer.liquid. See that file's header for the data model.

  - Lazy-loads a pinned, current <model-viewer> (only if none is already defined).
  - Hotspot tooltips, feature list, dimension lines, optional animation, and the
    full-screen <dialog>.
  - Camera views come from the GLB itself: every KeyShot camera named "3D - …"
    becomes a view button (prefix stripped), in file order; the first is the
    default view. Orthographic cameras are converted to matching perspective
    framing. Other cameras (Hero shots, Free Camera, active) are ignored.
  - Publishes analytics events through Shopify.analytics.publish for a custom pixel.
  - Optional roughness override (data-roughness / data-roughness-match) so glossy
    KeyShot plastics read correctly on the site without changing the render materials.
  - Flomec3D.loadThree(): shared three.js loader for demos that need their own renderer
    (e.g. the ultrasonic cutaway). Loaded on demand, once per page.
*/
(() => {
  if (customElements.get('flomec-3d-viewer')) return;

  // Pin the version; check https://github.com/google/model-viewer/releases before upgrading.
  const MODEL_VIEWER_SRC = 'https://ajax.googleapis.com/ajax/libs/model-viewer/4.0.0/model-viewer.min.js';
  const DESKTOP = window.matchMedia('(min-width: 768px)');
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  const SVG_NS = 'http://www.w3.org/2000/svg';

  let loader = null;
  function loadModelViewer() {
    if (customElements.get('model-viewer')) return Promise.resolve();
    if (!loader) {
      loader = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.type = 'module';
        script.src = MODEL_VIEWER_SRC;
        script.onload = () => customElements.whenDefined('model-viewer').then(resolve);
        script.onerror = reject;
        document.head.appendChild(script);
      });
    }
    return loader;
  }

  // three.js for demos that need more than <model-viewer> can do (cutaways, flowing water).
  // Loaded only when such a demo starts, once per page; later calls reuse it.
  // r128 is the last release with classic (non-module) scripts. Upgrading means switching
  // the demos to module imports, so pin and change all of these together.
  const THREE_SRC = [
    'https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js',
    'https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/controls/OrbitControls.js',
    'https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/loaders/GLTFLoader.js',
    'https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/loaders/DRACOLoader.js',
  ];
  const THREE_DRACO_PATH = 'https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/libs/draco/gltf/';
  let threeLoader = null;
  function loadThree() {
    const T = window.THREE;
    if (T?.GLTFLoader && T?.OrbitControls && T?.DRACOLoader) return Promise.resolve(T);
    if (!threeLoader) {
      // In order: the loaders need the core THREE global to exist first
      threeLoader = THREE_SRC.reduce((chain, src) => chain.then(() => new Promise((resolve, reject) => {
        if (src === THREE_SRC[0] && window.THREE) return resolve();
        const script = document.createElement('script');
        script.src = src;
        script.onload = resolve;
        script.onerror = () => reject(new Error(`Could not load ${src}`));
        document.head.appendChild(script);
      })), Promise.resolve())
        .then(() => window.THREE)
        .catch((error) => {
          threeLoader = null; // allow a retry next time a demo starts
          throw error;
        });
    }
    return threeLoader;
  }

  function track(name, data = {}) {
    try {
      window.Shopify?.analytics?.publish?.(name, data);
    } catch (e) {
      /* analytics must never break the viewer */
    }
  }

  /* ---------- GLB camera → <model-viewer> view conversion ---------- */

  const VIEW_PREFIX = /^3D\s*-\s*/i;
  const DEFAULT_FOV = 27;
  const deg = (rad) => (rad * 180) / Math.PI;

  // Read the JSON chunk of a .glb. Called after model-viewer has loaded the file, so the
  // browser serves it from its HTTP cache instead of downloading it again. (Range requests
  // were unreliable here: the CDN can answer them with compressed bytes.)
  async function readGltfJson(url) {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`GLB request failed: ${response.status}`);
    const buffer = await response.arrayBuffer();
    const view = new DataView(buffer);
    if (buffer.byteLength < 20 || view.getUint32(0, true) !== 0x46546c67) throw new Error('Not a GLB file');
    const jsonLength = view.getUint32(12, true);
    return JSON.parse(new TextDecoder().decode(new Uint8Array(buffer, 20, jsonLength)));
  }

  // 4x4 column-major matrix helpers (glTF convention)
  function multiply(a, b) {
    const out = new Array(16).fill(0);
    for (let c = 0; c < 4; c++) {
      for (let r = 0; r < 4; r++) {
        for (let k = 0; k < 4; k++) out[c * 4 + r] += a[k * 4 + r] * b[c * 4 + k];
      }
    }
    return out;
  }
  function compose(t = [0, 0, 0], q = [0, 0, 0, 1], s = [1, 1, 1]) {
    const [x, y, z, w] = q;
    return [
      (1 - 2 * (y * y + z * z)) * s[0], 2 * (x * y + z * w) * s[0], 2 * (x * z - y * w) * s[0], 0,
      2 * (x * y - z * w) * s[1], (1 - 2 * (x * x + z * z)) * s[1], 2 * (y * z + x * w) * s[1], 0,
      2 * (x * z + y * w) * s[2], 2 * (y * z - x * w) * s[2], (1 - 2 * (x * x + y * y)) * s[2], 0,
      t[0], t[1], t[2], 1,
    ];
  }
  const transform = (m, p) => [0, 1, 2].map((i) => m[i] * p[0] + m[4 + i] * p[1] + m[8 + i] * p[2] + m[12 + i]);
  const normalize = (v) => { const l = Math.hypot(...v) || 1; return v.map((x) => x / l); };

  function viewsFromGltf(gltf) {
    const nodes = gltf.nodes || [];
    const parent = [];
    nodes.forEach((n, i) => (n.children || []).forEach((c) => { parent[c] = i; }));
    const cache = [];
    const world = (i) => {
      if (cache[i]) return cache[i];
      const n = nodes[i];
      const local = n.matrix ? n.matrix.slice() : compose(n.translation, n.rotation, n.scale);
      cache[i] = parent[i] != null ? multiply(world(parent[i]), local) : local;
      return cache[i];
    };

    // Model center = bounding-box center of all meshes (what the camera aims toward)
    const min = [Infinity, Infinity, Infinity];
    const max = [-Infinity, -Infinity, -Infinity];
    nodes.forEach((n, i) => {
      if (n.mesh == null) return;
      (gltf.meshes[n.mesh].primitives || []).forEach((prim) => {
        const acc = gltf.accessors[prim.attributes.POSITION];
        if (!acc?.min || !acc?.max) return;
        for (const x of [acc.min[0], acc.max[0]]) for (const y of [acc.min[1], acc.max[1]]) for (const z of [acc.min[2], acc.max[2]]) {
          transform(world(i), [x, y, z]).forEach((v, k) => { min[k] = Math.min(min[k], v); max[k] = Math.max(max[k], v); });
        }
      });
    });
    const center = min.map((v, k) => (v + max[k]) / 2);

    const cams = nodes
      .map((n, i) => ({ n, i }))
      .filter(({ n }) => n.camera != null && VIEW_PREFIX.test(n.name || ''));
    if (!cams.length || !Number.isFinite(center[0])) return { views: [], fov: DEFAULT_FOV };

    const firstPersp = cams.map(({ n }) => gltf.cameras[n.camera]).find((c) => c.type === 'perspective');
    const fov = firstPersp ? deg(firstPersp.perspective.yfov) : DEFAULT_FOV;
    const halfFov = (fov * Math.PI) / 360;

    const views = cams.map(({ n, i }) => {
      const m = world(i);
      const pos = [m[12], m[13], m[14]];
      const up = normalize([m[4], m[5], m[6]]);
      const forward = normalize([-m[8], -m[9], -m[10]]);
      const t = forward.reduce((sum, f, k) => sum + f * (center[k] - pos[k]), 0);
      const target = pos.map((p, k) => p + forward[k] * t);
      const v = pos.map((p, k) => p - target[k]);
      let radius = Math.hypot(...v);
      const phi = Math.acos(Math.max(-1, Math.min(1, v[1] / radius)));
      let theta = Math.atan2(v[0], v[2]);
      // Straight up/down views: azimuth is undefined, so take it from the camera's up vector
      if (deg(phi) < 1) theta = Math.atan2(-up[0], -up[2]);
      if (deg(phi) > 179) theta = Math.atan2(up[0], up[2]);
      const cam = gltf.cameras[n.camera];
      let aspect = 1;
      if (cam.type === 'orthographic') {
        radius = cam.orthographic.ymag / Math.tan(halfFov);
        aspect = cam.orthographic.xmag / cam.orthographic.ymag;
      } else if (cam.perspective?.aspectRatio) {
        aspect = cam.perspective.aspectRatio;
      }
      return {
        label: n.name.replace(VIEW_PREFIX, '').trim(),
        theta: Number(deg(theta).toFixed(2)),
        phi: Number(deg(phi).toFixed(2)),
        radius,
        aspect, // width/height the KeyShot camera was framed for
        target: target.map((x) => `${x.toFixed(4)}m`).join(' '),
      };
    });
    return { views, fov };
  }

  /* ---------- measurements ---------- */

  const TO_INCHES = { in: 1, inch: 1, inches: 1, '"': 1, ft: 12, foot: 12, feet: 12, mm: 1 / 25.4, cm: 1 / 2.54, m: 39.3701 };
  const METRIC = new Set(['mm', 'cm', 'm']);

  // "5.3 in", "5.3", 5.3, "135 mm" -> { num, unit, inches } (numbers without a unit are inches)
  function parseMeasure(value) {
    const match = String(value ?? '').trim().match(/(-?\d+(?:\.\d+)?)\s*([a-zA-Z"]*)/);
    if (!match) return null;
    const num = parseFloat(match[1]);
    const unit = (match[2] || 'in').toLowerCase();
    const factor = TO_INCHES[unit];
    if (!Number.isFinite(num) || !factor) return null;
    return { num, unit, inches: num * factor };
  }

  // "5.3 in (13.5 cm)" or "135 mm (5.3 in)"
  function formatMeasure(value) {
    const m = parseMeasure(value);
    if (!m) return String(value || '');
    const shown = `${m.num} ${m.unit === 'inch' || m.unit === 'inches' || m.unit === '"' ? 'in' : m.unit}`;
    return METRIC.has(m.unit)
      ? `${shown} (${m.inches.toFixed(1)} in)`
      : `${shown} (${(m.inches * 2.54).toFixed(1)} cm)`;
  }

  // Flexible parts that shouldn't count toward the product's size (by KeyShot part name)
  const EXCLUDE_FROM_SIZE = /wire|cable|cord|hose|lead/i;

  // Bounding box of the product body in model space, skipping excluded parts
  function measureBody(gltf) {
    const nodes = gltf.nodes || [];
    const parent = [];
    nodes.forEach((n, i) => (n.children || []).forEach((c) => { parent[c] = i; }));
    const excluded = (i) => {
      for (let k = i; k != null; k = parent[k]) if (EXCLUDE_FROM_SIZE.test(nodes[k].name || '')) return true;
      return false;
    };
    const cache = [];
    const world = (i) => {
      if (cache[i]) return cache[i];
      const n = nodes[i];
      const local = n.matrix ? n.matrix.slice() : compose(n.translation, n.rotation, n.scale);
      cache[i] = parent[i] != null ? multiply(world(parent[i]), local) : local;
      return cache[i];
    };
    const min = [Infinity, Infinity, Infinity];
    const max = [-Infinity, -Infinity, -Infinity];
    nodes.forEach((n, i) => {
      if (n.mesh == null || excluded(i)) return;
      (gltf.meshes[n.mesh].primitives || []).forEach((prim) => {
        const acc = gltf.accessors[prim.attributes.POSITION];
        if (!acc?.min || !acc?.max) return;
        for (const x of [acc.min[0], acc.max[0]]) for (const y of [acc.min[1], acc.max[1]]) for (const z of [acc.min[2], acc.max[2]]) {
          transform(world(i), [x, y, z]).forEach((v, k) => { min[k] = Math.min(min[k], v); max[k] = Math.max(max[k], v); });
        }
      });
    });
    return Number.isFinite(min[0]) ? { min, max } : null;
  }

  // Length / Width / Height lines around the body box, labeled from the product's metafields.
  // Returns null when the model's size doesn't agree with the metafields (lines would mislead).
  function autoDimensionLines(box, fields) {
    const size = box.max.map((v, k) => v - box.min[k]); // meters
    const lengthAxis = size[0] >= size[2] ? 0 : 2; // longer horizontal side
    const widthAxis = lengthAxis === 0 ? 2 : 0;
    const specs = {
      length: parseMeasure(fields.length),
      width: parseMeasure(fields.width),
      height: parseMeasure(fields.height),
    };
    const measured = { length: size[lengthAxis], width: size[widthAxis], height: size[1] };
    // Agreement check: within 10% or 0.15 in (covers CATSY's one-decimal rounding)
    for (const key of Object.keys(specs)) {
      const spec = specs[key];
      if (!spec) continue;
      const inches = measured[key] * 39.3701;
      if (Math.abs(inches - spec.inches) > Math.max(0.15, spec.inches * 0.1)) {
        console.warn(`[flomec-3d-viewer] Dimension lines skipped: model ${key} ${inches.toFixed(2)} in vs product data ${spec.inches.toFixed(2)} in`);
        return null;
      }
    }
    const gap = Math.max(...size) * 0.06; // keep lines just off the model
    const pos = (l, y, w) => {
      const p = [0, 0, 0];
      p[lengthAxis] = l;
      p[1] = y;
      p[widthAxis] = w;
      return p.map((v) => `${v.toFixed(4)}m`).join(' ');
    };
    const { min, max } = box;
    const outW = max[widthAxis] + gap;
    const outL = max[lengthAxis] + gap;
    const lines = [];
    if (specs.length) lines.push({ label: 'Length', value: fields.length, start: pos(min[lengthAxis], min[1], outW), end: pos(max[lengthAxis], min[1], outW) });
    if (specs.height) lines.push({ label: 'Height', value: fields.height, start: pos(min[lengthAxis], min[1], outW), end: pos(min[lengthAxis], max[1], outW) });
    if (specs.width) lines.push({ label: 'Width', value: fields.width, start: pos(outL, min[1], min[widthAxis]), end: pos(outL, min[1], max[widthAxis]) });
    return lines.length ? lines : null;
  }

  // Top-center point of the parts that use a material (matched by name prefix), in
  // model space: where a label for that part should sit. Returns "x y z" in meters.
  function partTop(gltf, materialPrefix) {
    const nodes = gltf.nodes || [];
    const parent = [];
    nodes.forEach((n, i) => (n.children || []).forEach((c) => { parent[c] = i; }));
    const cache = [];
    const world = (i) => {
      if (cache[i]) return cache[i];
      const n = nodes[i];
      const local = n.matrix ? n.matrix.slice() : compose(n.translation, n.rotation, n.scale);
      cache[i] = parent[i] != null ? multiply(world(parent[i]), local) : local;
      return cache[i];
    };
    const min = [Infinity, Infinity, Infinity];
    const max = [-Infinity, -Infinity, -Infinity];
    nodes.forEach((n, i) => {
      if (n.mesh == null) return;
      (gltf.meshes[n.mesh].primitives || []).forEach((prim) => {
        const mat = gltf.materials?.[prim.material];
        if (!mat || !(mat.name || '').startsWith(materialPrefix)) return;
        const acc = gltf.accessors[prim.attributes.POSITION];
        if (!acc?.min || !acc?.max) return;
        for (const x of [acc.min[0], acc.max[0]]) for (const y of [acc.min[1], acc.max[1]]) for (const z of [acc.min[2], acc.max[2]]) {
          transform(world(i), [x, y, z]).forEach((v, k) => { min[k] = Math.min(min[k], v); max[k] = Math.max(max[k], v); });
        }
      });
    });
    if (!Number.isFinite(min[0])) return null;
    return [(min[0] + max[0]) / 2, max[1], (min[2] + max[2]) / 2].map((v) => `${v.toFixed(4)}m`).join(' ');
  }

  /* ---------- demo registry ---------- */

  const DEMO_TYPES = {};
  const demoScripts = {};
  function registerDemo(type, factory) {
    DEMO_TYPES[type] = factory;
  }
  // Load assets/flomec-3d-demo-<type>.js once, the first time that demo starts
  function loadDemoType(type, url) {
    if (DEMO_TYPES[type]) return Promise.resolve();
    if (!url) return Promise.reject(new Error(`No script for demo type "${type}"`));
    if (!demoScripts[type]) {
      demoScripts[type] = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = url;
        script.onload = () => (DEMO_TYPES[type] ? resolve() : reject(new Error(`${url} did not register "${type}"`)));
        script.onerror = () => reject(new Error(`Could not load ${url}`));
        document.head.appendChild(script);
      });
    }
    return demoScripts[type];
  }

  // "0.1 - 15 ft/sec" -> { min: 0.1, max: 15, unit: 'ft/sec' }
  function parseRange(text) {
    const m = String(text || '').match(/(-?\d+(?:\.\d+)?)\s*[-–]\s*(-?\d+(?:\.\d+)?)\s*(.*)/);
    if (!m) return null;
    return { min: parseFloat(m[1]), max: parseFloat(m[2]), unit: m[3].trim() };
  }

  function escapeHtml(text) {
    return String(text ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  // "0.043m 0.0297m -0.0664m" -> [0.043, 0.0297, -0.0664]
  function parseVec(str) {
    return String(str || '').trim().split(/\s+/).map((v) => parseFloat(v));
  }

  /* ---------- roughness override ---------- */

  // Compare names on letters and digits only, so "Dark Gray PVC", "Dark_Gray_PVC" and
  // "dark-gray-pvc #2" all match. Exporters often swap spaces for underscores.
  const normName = (text) => String(text || '').toLowerCase().replace(/[^a-z0-9]/g, '');

  // "Plastic_Black, Housing:0.7" -> [{ match: 'plasticblack', value: null }, { match: 'housing', value: 0.7 }]
  function parseMaterialRules(text) {
    return String(text || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((entry) => {
        const i = entry.lastIndexOf(':');
        const num = i > -1 ? parseFloat(entry.slice(i + 1)) : NaN;
        return Number.isNaN(num)
          ? { match: normName(entry), value: null, label: entry }
          : { match: normName(entry.slice(0, i)), value: num, label: entry.slice(0, i).trim() };
      })
      .filter((r) => r.match);
  }

  // Display-only roughness from the viewer entry (Plastic Roughness / Roughness Materials).
  // Blank list = any material with "plastic" in its name. Does nothing when both are blank.
  // Matched materials are treated as plastic, so metalness and clearcoat are also set
  // to 0 and any roughness map is removed: KeyShot's glTF export sometimes marks
  // plastics as fully metallic (reads as chrome), adds a glossy clearcoat layer, or
  // bakes a roughness map (e.g. on parts with labels).
  function applyRoughness(modelViewer) {
    const materials = modelViewer?.model?.materials;
    if (!materials) return;
    const base = parseFloat(modelViewer.dataset.roughness);
    const rules = parseMaterialRules(modelViewer.dataset.roughnessMatch);
    if (rules.length && Number.isNaN(base) && rules.some((r) => r.value == null)) {
      console.warn('[Flomec3D] Roughness Materials is set, but no Plastic Roughness value reached the viewer (check the field key).');
    }
    if (!rules.length) rules.push({ match: 'plastic', value: null, label: 'plastic' });
    const used = new Set();

    materials.forEach((mat) => {
      const name = normName(mat.name);
      const rule = rules.find((r) => name.includes(r.match));
      if (!rule) return;
      used.add(rule);
      const value = rule.value ?? base;
      if (Number.isNaN(value)) return;
      const pbr = mat.pbrMetallicRoughness;
      pbr.setRoughnessFactor(Math.min(1, Math.max(0, value)));
      pbr.setMetallicFactor(0);
      // KeyShot can export a glossy clearcoat layer on top, which stays mirror-like
      // whatever the base roughness is
      if (mat.clearcoatFactor > 0) mat.setClearcoatFactor(0);
      // A roughness map would multiply the value instead of replacing it (0.7 x a glossy
      // map is still glossy), so drop the map and let the admin value win
      if (pbr.metallicRoughnessTexture?.texture) pbr.metallicRoughnessTexture.setTexture(null);
    });

    // Flag entries in Roughness Materials that matched nothing (usually a name mismatch)
    const hasSettings = !Number.isNaN(base) || rules.some((r) => r.value != null);
    const missed = rules.filter((r) => !used.has(r));
    if (hasSettings && missed.length) {
      console.warn(
        `[Flomec3D] Roughness Materials matched no material: ${missed.map((r) => `"${r.label}"`).join(', ')}.`,
        'Run Flomec3D.listMaterials() to see the names in this model.'
      );
    }
  }

  const round2 = (n) => (typeof n === 'number' ? +n.toFixed(2) : '');
  const loadedViewer = () => [...document.querySelectorAll('model-viewer')].find((mv) => mv.loaded && mv.model);
  const NOT_LOADED = 'No 3D model has loaded yet. Rotate the model or open Explore in 3D, then run this again.';

  // Console helper for filling in Roughness Materials: Flomec3D.listMaterials()
  // Returns the list as well as printing it, so it shows even when the console hides logs.
  // The gallery renders more than one viewer, so it reads whichever one has loaded.
  function listMaterials() {
    const mv = loadedViewer();
    if (!mv) return NOT_LOADED;
    const rows = mv.model.materials.map((m) => ({
      name: m.name,
      roughness: round2(m.pbrMetallicRoughness.roughnessFactor),
      metalness: round2(m.pbrMetallicRoughness.metallicFactor),
      clearcoat: round2(m.clearcoatFactor),
      specular: round2(m.specularFactor),
      ior: round2(m.ior),
      roughnessMap: m.pbrMetallicRoughness.metallicRoughnessTexture?.texture ? 'yes' : '',
    }));
    console.table(rows);
    return rows;
  }

  // Console helper for finding which part uses a material: Flomec3D.highlightMaterial('Opaque(5,5,5)')
  // Paints every matching material magenta. Run Flomec3D.highlightMaterial() to restore.
  const highlighted = new Map();
  function highlightMaterial(fragment) {
    const mv = loadedViewer();
    if (!mv) return NOT_LOADED;
    highlighted.forEach((color, mat) => mat.pbrMetallicRoughness.setBaseColorFactor(color));
    highlighted.clear();
    if (!fragment) return 'Colors restored.';
    const match = normName(fragment);
    const hits = mv.model.materials.filter((m) => normName(m.name).includes(match));
    hits.forEach((m) => {
      highlighted.set(m, [...m.pbrMetallicRoughness.baseColorFactor]);
      m.pbrMetallicRoughness.setBaseColorFactor([1, 0, 1, 1]);
    });
    return hits.length ? hits.map((m) => m.name) : `No material name contains "${fragment}".`;
  }



  class Flomec3dViewer extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.model = this.querySelector('model-viewer');
      this.inlineStage = this.querySelector('[data-stage="inline"]');
      this.dialogStage = this.querySelector('[data-stage="dialog"]');
      this.dialog = this.querySelector('[data-dialog]');
      this.status = this.querySelector('[data-status]');
      this.errorEl = this.querySelector('[data-error]');
      this.progress = this.querySelector('[data-progress]');
      this.sku = this.dataset.sku || '';
      this.hotspots = [...this.model.querySelectorAll('[data-hotspot]')];
      this.features = [...this.querySelectorAll('[data-action="feature"]')];
      this.viewButtons = [];
      this.viewsEl = this.querySelector('[data-views]');
      this.defaultView = null;
      this.fov = null;
      this.dimsOn = false;
      this.openedFromGallery = false;

      try {
        this.config = JSON.parse(this.querySelector('[data-config]')?.textContent || '{}');
      } catch (e) {
        this.config = {};
      }

      if (REDUCED_MOTION.matches) {
        this.model.setAttribute('interaction-prompt', 'none');
        this.model.setAttribute('interpolation-decay', '1');
      }

      this.buildDimensions();
      this.bindEvents();
      this.observeInline();
    }

    /* ---------- loading ---------- */

    observeInline() {
      if (!('IntersectionObserver' in window)) {
        this.ensureLoaded();
        return;
      }
      const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 200));
      let started = false;
      const start = () => {
        if (started) return;
        started = true;
        this.model.setAttribute('loading', 'eager');
        this.ensureLoaded();
      };
      // Desktop: once the gallery is on screen, preload while the browser is idle, so the
      // model is ready (at its KeyShot starting view) before the 3D slide is chosen.
      // Mobile: wait until the 3D slide itself comes into view, to save data.
      const target = DESKTOP.matches ? this.closest('.js-slider') || this.inlineStage : this.inlineStage;
      const io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          io.disconnect();
          if (DESKTOP.matches) idle(start);
          else start();
        }
      }, { threshold: 0 });
      io.observe(target);
      // Safety net: the first touch or click on the 3D slide always starts loading
      this.inlineStage.addEventListener('pointerdown', start, { once: true });
    }

    ensureLoaded() {
      return loadModelViewer().catch(() => this.showError());
    }

    loadViews() {
      if (this.viewsPromise) return this.viewsPromise;
      const src = this.model.getAttribute('src');
      this.viewsPromise = readGltfJson(src)
        .then((gltf) => {
          this.gltf = gltf;
          const { views, fov } = viewsFromGltf(gltf);
          this.fov = `${fov.toFixed(2)}deg`;
          this.buildViews(views);
          this.defaultView = views[0] || null;
          this.applyView(this.defaultView, true);
          // The starting view shows as selected, so it's clear the chips are buttons
          if (this.viewButtons[0]) this.markView(this.viewButtons[0]);
          this.buildAutoDimensions(gltf);
        })
        .catch((error) => {
          // No usable cameras: the viewer frames the model automatically
          console.warn('[flomec-3d-viewer] Could not read camera views from the GLB:', error);
        });
      return this.viewsPromise;
    }

    // Automatic dimensions (unless this model has hand-placed lines in its viewer entry)
    buildAutoDimensions(gltf) {
      if (this.config.dimensions?.length) return;
      const fields = this.config.autoDimensions || {};
      if (!fields.length && !fields.width && !fields.height) return;
      const box = measureBody(gltf);
      const lines = box && autoDimensionLines(box, fields);
      if (!lines) return;
      this.buildDimensions(lines);
      const btn = this.querySelector('[data-action="dims"]');
      if (btn) btn.hidden = false;
    }

    buildViews(views) {
      if (!this.viewsEl || !views.length) return;
      views.forEach((v, i) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'flomec-3d-viewer__chip';
        btn.setAttribute('role', 'radio');
        btn.setAttribute('aria-checked', 'false');
        btn.tabIndex = i === 0 ? 0 : -1;
        btn.dataset.action = 'view';
        btn.dataset.label = v.label;
        btn.dataset.index = String(i);
        btn.textContent = v.label;
        this.viewsEl.appendChild(btn);
      });
      this.views = views;
      this.viewButtons = [...this.viewsEl.querySelectorAll('[data-action="view"]')];
      this.viewsEl.hidden = false;
    }

    // KeyShot framed each camera for its own aspect ratio (e.g. square or 4:3). In a
    // narrower viewer, like a phone held upright, pull the camera back so the same
    // width still fits instead of the model overflowing the sides.
    orbitFor(view) {
      const w = this.model.clientWidth || 1;
      const h = this.model.clientHeight || 1;
      const scale = Math.max(1, (view.aspect || 1) / (w / h));
      return `${view.theta}deg ${view.phi}deg ${(view.radius * scale).toFixed(4)}m`;
    }

    // Set a view; `instant` skips the camera animation (used for the starting view)
    applyView(view, instant = false) {
      if (this.fov) this.model.setAttribute('field-of-view', this.fov);
      this.activeView = view || null;
      if (view) {
        this.model.setAttribute('camera-target', view.target);
        this.model.setAttribute('camera-orbit', view.orbit || this.orbitFor(view));
      } else {
        this.model.setAttribute('camera-target', 'auto auto auto');
        this.model.setAttribute('camera-orbit', 'auto auto auto');
      }
      if (instant && typeof this.model.jumpCameraToGoal === 'function') this.model.jumpCameraToGoal();
    }

    showError() {
      if (this.errorEl) this.errorEl.hidden = false;
    }

    /* ---------- events ---------- */

    bindEvents() {
      this.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-action]');
        if (btn && this.contains(btn)) {
          this.handleAction(btn.dataset.action, btn);
          return;
        }
        const hotspotBtn = event.target.closest('.flomec-3d-viewer__hotspot-btn');
        if (hotspotBtn) {
          const index = this.hotspots.indexOf(hotspotBtn.closest('[data-hotspot]'));
          this.toggleHotspot(index);
          return;
        }
        // Click on the model itself (not a hotspot or tooltip) closes any open tooltip.
        if (event.target === this.model) this.closeHotspots();
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.openIndex != null && !this.dialog.open) {
          this.closeHotspots(true);
        }
      });

      const views = this.querySelector('[data-views]');
      views?.addEventListener('keydown', (event) => this.onViewKeydown(event));

      // Esc on the dialog: close an open tooltip first, the dialog second.
      this.dialog.addEventListener('cancel', (event) => {
        if (this.demo) {
          event.preventDefault();
          this.stopDemo();
          return;
        }
        if (this.openIndex != null) {
          event.preventDefault();
          this.closeHotspots(true);
        }
      });
      this.dialog.addEventListener('close', () => this.afterClose());

      this.model.addEventListener('progress', (event) => {
        const p = event.detail?.totalProgress ?? 0;
        if (!this.progress) return;
        this.progress.style.setProperty('--flomec-3d-progress', p);
        this.progress.classList.toggle('is-done', p >= 1);
      });
      // reveal="manual": keep the poster up until the KeyShot starting view is applied,
      // so the model never appears at a default angle and then jumps.
      this.model.addEventListener('load', async () => {
        // Before the poster lifts, so the original gloss never flashes on screen
        applyRoughness(this.model);
        await this.loadViews();
        this.applyView(this.defaultView, true);
        this.model.dismissPoster?.();
        this.classList.add('is-loaded');
        this.updateDimensions();
      });
      this.model.addEventListener('error', () => this.showError());
      this.hint = this.querySelector('[data-hint]');
      this.model.addEventListener('camera-change', (event) => {
        // Hotspot positions update during the render, so measure on the next frame
        if (this.dimsOn) requestAnimationFrame(() => this.updateDimensions());
        // Keep an open tooltip on screen as the model turns
        if (this.openIndex != null && !this._tipFrame) {
          this._tipFrame = requestAnimationFrame(() => {
            this._tipFrame = null;
            const open = this.hotspots[this.openIndex];
            if (open) this.placeTip(open);
          });
        }
        if (event.detail?.source === 'user-interaction') {
          // Fade the "Drag to rotate" hint once the shopper has interacted in the popup
          if (this.dialog.open) this.hint?.classList.add('is-faded');
          // The model is no longer at a named view, so no view button should look selected
          this.clearView();
        }
        if (!this.openedFromGallery && event.detail?.source === 'user-interaction' && !this.dialog.open) {
          this.openedFromGallery = true;
          track('flomec_3d_opened', { source: 'gallery', product_sku: this.sku });
        }
      });

      if ('ResizeObserver' in window) {
        new ResizeObserver(() => {
          // Re-fit the current named view when the viewer changes size (popup open/close,
          // phone rotation), then redraw any dimension lines
          if (this.activeView) this.applyView(this.activeView, true);
          if (this.dimsOn) requestAnimationFrame(() => this.updateDimensions());
        }).observe(this.model);
      }
    }

    handleAction(action, btn) {
      switch (action) {
        case 'open': this.open(btn); break;
        case 'close': this.dialog.close(); break;
        case 'zoom-in': this.zoomBy(4); break;
        case 'zoom-out': this.zoomBy(-4); break;
        case 'reset': this.resetView(); break;
        case 'dims': this.toggleDimensions(btn); break;
        case 'play': this.togglePlay(btn); break;
        case 'view': this.selectView(btn); break;
        case 'feature': this.openHotspot(Number(btn.dataset.index)); break;
        case 'demo': this.startDemo(Number(btn.dataset.demo), btn); break;
        case 'demo-stop': this.stopDemo(); break;
      }
    }

    // Zoom buttons move the camera closer/farther directly; model-viewer's zoom()
    // can stall when the field of view is set explicitly (from the KeyShot cameras).
    zoomBy(direction) {
      const orbit = this.model.getCameraOrbit?.();
      if (!orbit) return;
      const radius = orbit.radius * (direction > 0 ? 0.8 : 1.25);
      this.model.cameraOrbit = `${orbit.theta}rad ${orbit.phi}rad ${radius}m`;
    }

    /* ---------- full-screen dialog ---------- */

    open(trigger) {
      this.trigger = trigger;
      this.dialogStage.prepend(this.model);
      this.hint?.classList.remove('is-faded');
      this.dialog.showModal();
      // If the model isn't at one of the named views, start the popup at the default view
      if (this.defaultView && !this.viewButtons.some((b) => b.classList.contains('is-active'))) {
        this.applyView(this.defaultView);
        this.markView(this.viewButtons[0]);
      }
      document.documentElement.classList.add('flomec-3d-viewer-open');
      this.ensureLoaded();
      this.dialog.querySelector('[data-action="close"]')?.focus();
      track('flomec_3d_opened', { source: 'explore', product_sku: this.sku });
    }

    afterClose() {
      this.stopDemo();
      this.closeHotspots();
      if (this.dimsOn) this.toggleDimensions(this.querySelector('[data-action="dims"]'));
      this.inlineStage.prepend(this.model);
      document.documentElement.classList.remove('flomec-3d-viewer-open');
      this.trigger?.focus();
    }

    /* ---------- hotspots ---------- */

    toggleHotspot(index) {
      if (this.openIndex === index) this.closeHotspots();
      else this.openHotspot(index);
    }

    openHotspot(index) {
      const hotspot = this.hotspots[index];
      if (!hotspot) return;
      if (this.dimsOn) this.toggleDimensions(this.querySelector('[data-action="dims"]'));
      this.closeHotspots();

      const tip = hotspot.querySelector('[data-tip]');
      const btn = hotspot.querySelector('.flomec-3d-viewer__hotspot-btn');
      hotspot.classList.add('is-open');
      tip.hidden = false;
      btn.setAttribute('aria-expanded', 'true');
      this.openIndex = index;
      this.placeTip(hotspot);

      this.features.forEach((f, i) => {
        f.setAttribute('aria-pressed', String(i === index));
        f.classList.toggle('is-selected', i === index);
      });

      if (hotspot.dataset.orbit) {
        this.clearView();
        this.model.cameraOrbit = hotspot.dataset.orbit;
      }

      const parts = ['[data-tip-value]', '[data-tip-text]']
        .map((sel) => tip.querySelector(sel)?.textContent.trim())
        .filter(Boolean);
      this.announce(parts.length ? `${hotspot.dataset.label}: ${parts.join('. ')}` : hotspot.dataset.label);
      track('flomec_3d_hotspot_click', { hotspot_label: hotspot.dataset.label, product_sku: this.sku });
    }

    closeHotspots(returnFocus = false) {
      const wasOpen = this.hotspots[this.openIndex];
      this.hotspots.forEach((h) => {
        h.classList.remove('is-open', 'is-flipped', 'is-above');
        h.querySelector('[data-tip]').hidden = true;
        h.querySelector('.flomec-3d-viewer__hotspot-btn').setAttribute('aria-expanded', 'false');
      });
      this.features.forEach((f) => {
        f.setAttribute('aria-pressed', 'false');
        f.classList.remove('is-selected');
      });
      this.openIndex = null;
      if (returnFocus && wasOpen) wasOpen.querySelector('.flomec-3d-viewer__hotspot-btn').focus();
    }

    // Keep the tooltip fully inside the viewer: pick the side with more room, open upward
    // near the bottom, cap its width on narrow screens, then nudge it back in if needed.
    placeTip(hotspot) {
      const tip = hotspot.querySelector('[data-tip]');
      if (!tip || tip.hidden) return;
      const EDGE = 8;
      const box = this.model.getBoundingClientRect();
      const spot = hotspot.getBoundingClientRect();
      tip.style.translate = '';
      tip.style.maxWidth = `${Math.max(160, box.width - EDGE * 2)}px`;

      const roomRight = box.right - spot.right;
      const roomLeft = spot.left - box.left;
      hotspot.classList.remove('is-flipped', 'is-above');
      let r = tip.getBoundingClientRect();
      if (r.right > box.right - EDGE && roomLeft > roomRight) hotspot.classList.add('is-flipped');
      if (spot.top - box.top > box.height * 0.55) hotspot.classList.add('is-above');

      r = tip.getBoundingClientRect();
      let dx = 0;
      let dy = 0;
      if (r.right > box.right - EDGE) dx = box.right - EDGE - r.right;
      if (r.left + dx < box.left + EDGE) dx = box.left + EDGE - r.left;
      if (r.bottom > box.bottom - EDGE) dy = box.bottom - EDGE - r.bottom;
      if (r.top + dy < box.top + EDGE) dy = box.top + EDGE - r.top;
      if (dx || dy) tip.style.translate = `${Math.round(dx)}px ${Math.round(dy)}px`;
    }

    announce(text) {
      if (!this.status) return;
      this.status.textContent = '';
      requestAnimationFrame(() => { this.status.textContent = text; });
    }

    /* ---------- camera views ---------- */

    // Highlight a view button (radio state + roving tabindex) without moving the camera
    markView(btn) {
      this.viewButtons.forEach((b) => {
        const on = b === btn;
        b.setAttribute('aria-checked', String(on));
        b.tabIndex = on ? 0 : -1;
        b.classList.toggle('is-active', on);
      });
    }

    clearView() {
      this.activeView = null;
      if (!this.viewButtons.some((b) => b.classList.contains('is-active'))) return;
      this.viewButtons.forEach((b, i) => {
        b.setAttribute('aria-checked', 'false');
        b.classList.remove('is-active');
        b.tabIndex = i === 0 ? 0 : -1;
      });
    }

    selectView(btn) {
      this.markView(btn);
      this.closeHotspots();
      this.applyView(this.views[Number(btn.dataset.index)]);
      this.announce(`${btn.dataset.label} view`);
      track('flomec_3d_view_select', { view_name: btn.dataset.label, product_sku: this.sku });
    }

    onViewKeydown(event) {
      const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
      if (!keys.includes(event.key)) return;
      event.preventDefault();
      const list = this.viewButtons;
      const current = list.indexOf(document.activeElement);
      let next = current;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (current + 1) % list.length;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (current - 1 + list.length) % list.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = list.length - 1;
      list[next].focus();
      this.selectView(list[next]);
    }

    resetView() {
      this.closeHotspots();
      this.applyView(this.defaultView);
      if (this.viewButtons[0]) this.markView(this.viewButtons[0]);
      this.announce('View reset');
    }

    /* ---------- dimensions ---------- */

    buildDimensions(list = this.config.dimensions || []) {
      this.dims = [];
      const svg = this.model.querySelector('[data-dims]');
      list.forEach((d, i) => {
        const a = parseVec(d.start);
        const b = parseVec(d.end);
        if (a.length < 3 || b.length < 3 || a.concat(b).some(Number.isNaN)) return;
        const mid = a.map((v, k) => (v + b[k]) / 2);

        const point = (slot, pos) => {
          const el = document.createElement('div');
          el.className = 'flomec-3d-viewer__dim-point';
          el.slot = slot;
          el.dataset.dim = '';
          el.dataset.position = pos;
          this.model.appendChild(el);
          return el;
        };
        point(`hotspot-dim-${i}-a`, d.start);
        point(`hotspot-dim-${i}-b`, d.end);

        const label = document.createElement('div');
        label.className = 'flomec-3d-viewer__dim-label';
        label.slot = `hotspot-dim-${i}-label`;
        label.dataset.dim = '';
        label.dataset.position = mid.map((v) => `${v}m`).join(' ');
        const name = String(d.label || '').toUpperCase();
        const value = d.value ? formatMeasure(d.value) : '';
        label.textContent = value ? `${name}: ${value}` : name;
        this.model.appendChild(label);

        const line = document.createElementNS(SVG_NS, 'line');
        line.setAttribute('class', 'flomec-3d-viewer__dim-line');
        svg.appendChild(line);
        this.dims.push({ i, line, label: d.label, value: d.value ? formatMeasure(d.value) : '' });
      });
    }

    // Draw each line between its two endpoint hotspots, measured from where model-viewer
    // actually placed them on screen (canvasPosition can be scaled by render resolution).
    updateDimensions() {
      if (!this.dims?.length || !this.dimsOn) return;
      const svg = this.model.querySelector('[data-dims]');
      if (!svg) return;
      const box = svg.getBoundingClientRect();
      const point = (slot) => {
        const el = this.model.querySelector(`[slot="${slot}"]`);
        if (!el) return null;
        const r = el.getBoundingClientRect();
        return { x: r.left + r.width / 2 - box.left, y: r.top + r.height / 2 - box.top };
      };
      this.dims.forEach(({ i, line }) => {
        const a = point(`hotspot-dim-${i}-a`);
        const b = point(`hotspot-dim-${i}-b`);
        if (!a || !b) return;
        line.setAttribute('x1', a.x);
        line.setAttribute('y1', a.y);
        line.setAttribute('x2', b.x);
        line.setAttribute('y2', b.y);
      });
    }

    toggleDimensions(btn) {
      this.dimsOn = !this.dimsOn;
      this.classList.toggle('is-dims', this.dimsOn);
      btn?.setAttribute('aria-pressed', String(this.dimsOn));
      if (this.dimsOn) {
        this.closeHotspots();
        requestAnimationFrame(() => requestAnimationFrame(() => this.updateDimensions()));
        this.announce(this.dims.map((d) => (d.value ? `${d.label}: ${d.value}` : d.label)).join('. '));
        track('flomec_3d_dimensions', { product_sku: this.sku });
      }
    }

    /* ---------- product demos: generic host ---------- */
    // Each demo type (leds, display, accuracy, …) lives in its own asset file,
    // assets/flomec-3d-demo-<type>.js, loaded only when a shopper starts it. The file
    // registers itself with Flomec3D.registerDemo(type, factory). The host here does
    // the shared work: popup, camera, panel + Stop, frame loop, cleanup, a11y, analytics.

    modelReady() {
      if (this.model.loaded) return Promise.resolve();
      return new Promise((resolve) => this.model.addEventListener('load', resolve, { once: true }));
    }

    async startDemo(index, trigger) {
      const entry = this.config.demos?.[index];
      const type = entry?.demo?.type;
      if (!type) return;
      this.stopDemo();
      // Demos run in the popup, where there's room for the controls
      if (!this.dialog.open) this.open(trigger);
      try {
        await Promise.all([this.ensureLoaded(), loadDemoType(type, entry.script)]);
        await this.modelReady();
        await this.loadViews();
      } catch (error) {
        console.warn(`[flomec-3d-viewer] Demo "${type}" could not load:`, error);
        return;
      }

      const panel = this.querySelector('[data-demo-panel]');
      if (!panel) return;
      panel.innerHTML = `
        <div class="flomec-3d-viewer__demo-head">
          <p class="flomec-3d-viewer__demo-title">${escapeHtml(entry.label)}</p>
          <button type="button" class="btn btn--plain btn--smaller" data-action="demo-stop">Stop demo</button>
        </div>
        <div class="flomec-3d-viewer__demo-body" data-demo-body></div>`;

      const frames = new Set();
      const labels = [];
      const run = { frames, labels };
      const host = {
        model: this.model,
        body: panel.querySelector('[data-demo-body]'),
        id: `${this.model.id}-demo`,
        values: entry.values || {},
        reducedMotion: REDUCED_MOTION.matches,
        announce: (text) => this.announce(text),
        findMaterial: (prefix) => (this.model.model?.materials || []).find((m) => (m.name || '').startsWith(prefix)),
        onFrame: (fn) => frames.add(fn),
        // Where a part sits on the model (top center), found by its material name
        partPosition: (materialPrefix) => (this.gltf ? partTop(this.gltf, materialPrefix) : null),
        // A small label pinned to a 3D point; removed automatically when the demo stops
        addLabel: (position, className = '') => {
          const el = document.createElement('div');
          el.className = `flomec-3d-viewer__demo-label ${className}`.trim();
          el.slot = `hotspot-demo-${labels.length}`;
          el.dataset.position = position;
          this.model.appendChild(el);
          labels.push(el);
          return el;
        },
        stop: () => this.stopDemo(),
        util: { parseRange, parseMeasure, escapeHtml },
      };

      let instance;
      try {
        instance = DEMO_TYPES[type](host, entry.demo, entry) || {};
      } catch (error) {
        console.warn(`[flomec-3d-viewer] Demo "${type}" failed to start:`, error);
        panel.innerHTML = '';
        return;
      }
      this.demo = { instance, run, type };

      this.closeHotspots();
      if (this.dimsOn) this.toggleDimensions(this.querySelector('[data-action="dims"]'));
      this.classList.add('is-demo');
      panel.hidden = false;
      this.focusHotspot(entry.hotspot);
      panel.querySelector('[data-action="demo-stop"]').focus();

      const tick = (time) => {
        frames.forEach((fn) => fn(time));
        run.frame = requestAnimationFrame(tick);
      };
      run.frame = requestAnimationFrame(tick);
      track('flomec_3d_demo', { product_sku: this.sku, demo_type: type, demo: entry.title });
    }

    stopDemo() {
      if (!this.demo) return;
      cancelAnimationFrame(this.demo.run.frame);
      try {
        this.demo.instance.stop?.();
      } catch (error) {
        console.warn('[flomec-3d-viewer] Demo cleanup failed:', error);
      }
      this.demo.run.labels.forEach((el) => el.remove());
      this.demo = null;
      this.classList.remove('is-demo');
      const panel = this.querySelector('[data-demo-panel]');
      if (panel) {
        panel.hidden = true;
        panel.innerHTML = '';
      }
      this.announce('Demo stopped');
    }

    // Turn the camera toward the demo's hotspot and move in closer
    focusHotspot(index) {
      const spot = this.hotspots[index];
      const orbit = this.model.getCameraOrbit?.();
      if (!spot || !orbit) return;
      this.clearView();
      this.model.cameraTarget = spot.dataset.position;
      this.model.cameraOrbit = `${orbit.theta}rad 40deg ${(orbit.radius * 0.45).toFixed(4)}m`;
    }

    /* ---------- animation ---------- */

    togglePlay(btn) {
      const name = this.config.animation;
      if (!name) return;
      const label = btn.querySelector('[data-play-label]');
      if (this.model.paused === false) {
        this.model.pause();
        btn.setAttribute('aria-pressed', 'false');
        if (label) label.textContent = 'Play animation';
        return;
      }
      this.model.animationName = name;
      this.model.play({ repetitions: 1 });
      btn.setAttribute('aria-pressed', 'true');
      if (label) label.textContent = 'Pause animation';
      this.model.addEventListener('finished', () => {
        btn.setAttribute('aria-pressed', 'false');
        if (label) label.textContent = 'Play animation';
      }, { once: true });
    }
  }

  customElements.define('flomec-3d-viewer', Flomec3dViewer);

  // Shared with the 3D Placer tool (sections/flomec__3d-placer.liquid)
  window.Flomec3D = { loadModelViewer, loadThree, THREE_DRACO_PATH, readGltfJson, viewsFromGltf, registerDemo, applyRoughness, listMaterials, highlightMaterial };
})();
